
import { Record } from "../../fable_modules/fable-library-js.5.13.0/Types.js";
import { record_type, int32_type, array_type, uint32_type } from "../../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { iterate } from "../../fable_modules/fable-library-js.5.13.0/Map.js";
import { fromInt64, toUInt32, op_RightShift, toUInt32_unchecked, op_Addition, fromUInt32, toUInt64_unchecked, toInt32, fromInt32, fromZero, compare } from "../../fable_modules/fable-library-js.5.13.0/BigInt.js";
import { twosComp, convertBigintToUInt32 } from "../NumberHelpers.js";
import { initialize, item, setItem } from "../../fable_modules/fable-library-js.5.13.0/Array.js";
import { head, tail, isEmpty, map2, toArray } from "../../fable_modules/fable-library-js.5.13.0/List.js";

class U32Region extends Record {
    constructor(A, B) {
        super();
        this.A = A;
        this.B = (B | 0);
    }
}

function U32Region_$reflection() {
    return record_type("EvalCompiled.U32Region", [], U32Region, () => [["A", array_type(uint32_type)], ["B", int32_type]]);
}

const noRegion = new U32Region(new Uint32Array(0), 0);

const maxRomTableAddressWidth = 16;

function romTable(mem) {
    const table = new Uint32Array(1 << mem.AddressWidth);
    iterate((addr, value) => {
        if ((compare(addr, fromZero()) >= 0) && (compare(addr, fromInt32(table.length)) < 0)) {
            setItem(table, ~~toInt32(addr), convertBigintToUInt32(mem.WordWidth, value));
        }
    }, mem.Data);
    return table;
}

function split2U(fc, _arg, _arg_1) {
    let src;
    const fc_1 = fc;
    src = (new U32Region(item(0, fc_1.InputLinks).UInt32Slab, item(0, fc_1.InputLinks).StepBase));
    let out0;
    const fc_2 = fc;
    out0 = (new U32Region(item(0, fc_2.Outputs).UInt32Slab, item(0, fc_2.Outputs).StepBase));
    let out1;
    const fc_3 = fc;
    out1 = (new U32Region(item(1, fc_3.Outputs).UInt32Slab, item(1, fc_3.Outputs).StepBase));
    return (step) => {
        const s = step.SimStep | 0;
        let bits;
        const r = src;
        bits = (r.A[(r.B + s)]);
        const r_1 = out0;
        const v = ((bits >>> _arg[0]) & _arg[1]) >>> 0;
        r_1.A[(r_1.B + s)] = v;
        const r_2 = out1;
        const v_1 = ((bits >>> _arg_1[0]) & _arg_1[1]) >>> 0;
        r_2.A[(r_2.B + s)] = v_1;
    };
}

function merge2U(fc, shift) {
    let a;
    const fc_1 = fc;
    a = (new U32Region(item(0, fc_1.InputLinks).UInt32Slab, item(0, fc_1.InputLinks).StepBase));
    let b;
    const fc_2 = fc;
    b = (new U32Region(item(1, fc_2.InputLinks).UInt32Slab, item(1, fc_2.InputLinks).StepBase));
    let dst;
    const fc_3 = fc;
    dst = (new U32Region(item(0, fc_3.Outputs).UInt32Slab, item(0, fc_3.Outputs).StepBase));
    return (step) => {
        let r, r_1;
        const s = step.SimStep | 0;
        const r_2 = dst;
        const v = (((((r = b, r.A[(r.B + s)])) << shift) >>> 0) | ((r_1 = a, r_1.A[(r_1.B + s)]))) >>> 0;
        r_2.A[(r_2.B + s)] = v;
    };
}

function demuxU(fc, n) {
    let src;
    const fc_1 = fc;
    src = (new U32Region(item(0, fc_1.InputLinks).UInt32Slab, item(0, fc_1.InputLinks).StepBase));
    let sel;
    const fc_2 = fc;
    sel = (new U32Region(item(1, fc_2.InputLinks).UInt32Slab, item(1, fc_2.InputLinks).StepBase));
    const outs = initialize(n, (i_2) => {
        const fc_3 = fc;
        const i_3 = i_2 | 0;
        return new U32Region(item(i_3, fc_3.Outputs).UInt32Slab, item(i_3, fc_3.Outputs).StepBase);
    });
    return (step) => {
        let r;
        const s = step.SimStep | 0;
        const selected = ~~((r = sel, r.A[(r.B + s)])) | 0;
        let bits;
        const r_1 = src;
        bits = (r_1.A[(r_1.B + s)]);
        for (let i_4 = 0; i_4 <= (n - 1); i_4++) {
            const r_2 = outs[i_4];
            const v = (i_4 === selected) ? bits : 0;
            r_2.A[(r_2.B + s)] = v;
        }
    };
}

function adderU(fc, hasCin, hasCout) {
    const firstOperand = (hasCin ? 1 : 0) | 0;
    let cin;
    if (hasCin) {
        const fc_1 = fc;
        cin = (new U32Region(item(0, fc_1.InputLinks).UInt32Slab, item(0, fc_1.InputLinks).StepBase));
    }
    else {
        cin = noRegion;
    }
    let a;
    const fc_2 = fc;
    const i_1 = firstOperand | 0;
    a = (new U32Region(item(i_1, fc_2.InputLinks).UInt32Slab, item(i_1, fc_2.InputLinks).StepBase));
    let b;
    const fc_3 = fc;
    const i_2 = (firstOperand + 1) | 0;
    b = (new U32Region(item(i_2, fc_3.InputLinks).UInt32Slab, item(i_2, fc_3.InputLinks).StepBase));
    let sumOut;
    const fc_4 = fc;
    sumOut = (new U32Region(item(0, fc_4.Outputs).UInt32Slab, item(0, fc_4.Outputs).StepBase));
    let coutOut;
    if (hasCout) {
        const fc_5 = fc;
        coutOut = (new U32Region(item(1, fc_5.Outputs).UInt32Slab, item(1, fc_5.Outputs).StepBase));
    }
    else {
        coutOut = noRegion;
    }
    const w = item(firstOperand + 1, fc.InputLinks).Width | 0;
    let mask;
    const w_1 = w | 0;
    mask = ((w_1 === 32) ? 4294967295 : (((1 << w_1) >>> 0) - 1));
    if (w === 32) {
        return (step) => {
            let r, r_1, r_2;
            const s = step.SimStep | 0;
            const carryIn = hasCin ? toUInt64_unchecked(fromUInt32((((r = cin, r.A[(r.B + s)])) & 1) >>> 0)) : (0n);
            const total = toUInt64_unchecked(op_Addition(toUInt64_unchecked(op_Addition(toUInt64_unchecked(fromUInt32((r_1 = a, r_1.A[(r_1.B + s)]))), toUInt64_unchecked(fromUInt32((r_2 = b, r_2.A[(r_2.B + s)]))))), carryIn));
            const r_3 = sumOut;
            const v = toUInt32_unchecked(total) >>> 0;
            r_3.A[(r_3.B + s)] = v;
            if (hasCout) {
                const r_4 = coutOut;
                const v_1 = ((toUInt32_unchecked(toUInt64_unchecked(op_RightShift(total, 32))) >>> 0) & 1) >>> 0;
                r_4.A[(r_4.B + s)] = v_1;
            }
        };
    }
    else {
        return (step_6) => {
            let r_5, r_6, r_7;
            const s_1 = step_6.SimStep | 0;
            const carryIn_1 = hasCin ? ((((r_5 = cin, r_5.A[(r_5.B + s_1)])) & 1) >>> 0) : 0;
            const total_1 = (((r_6 = a, r_6.A[(r_6.B + s_1)])) + ((r_7 = b, r_7.A[(r_7.B + s_1)]))) + carryIn_1;
            const r_8 = sumOut;
            const v_2 = (total_1 & mask) >>> 0;
            r_8.A[(r_8.B + s_1)] = v_2;
            if (hasCout) {
                const r_9 = coutOut;
                const v_3 = ((total_1 >>> w) & 1) >>> 0;
                r_9.A[(r_9.B + s_1)] = v_3;
            }
        };
    }
}

function counterU(fc, width, hasLoad, hasEnable) {
    let loadData;
    if (hasLoad) {
        const fc_1 = fc;
        loadData = (new U32Region(item(0, fc_1.InputLinks).UInt32Slab, item(0, fc_1.InputLinks).StepBase));
    }
    else {
        loadData = noRegion;
    }
    let load;
    if (hasLoad) {
        const fc_2 = fc;
        load = (new U32Region(item(1, fc_2.InputLinks).UInt32Slab, item(1, fc_2.InputLinks).StepBase));
    }
    else {
        load = noRegion;
    }
    let enable;
    if (hasEnable) {
        const fc_3 = fc;
        const i_2 = (hasLoad ? 2 : 0) | 0;
        enable = (new U32Region(item(i_2, fc_3.InputLinks).UInt32Slab, item(i_2, fc_3.InputLinks).StepBase));
    }
    else {
        enable = noRegion;
    }
    let dst;
    const fc_4 = fc;
    dst = (new U32Region(item(0, fc_4.Outputs).UInt32Slab, item(0, fc_4.Outputs).StepBase));
    return (step) => {
        let step_3, r_3, step_5, r_5;
        let lastOut;
        const step_1 = step;
        if (step_1.NumStep === 0) {
            lastOut = 0;
        }
        else {
            const r_1 = dst;
            lastOut = (r_1.A[(r_1.B + step_1.SimStepOld)]);
        }
        const r_8 = dst;
        let v;
        if (hasEnable && (((step_3 = step, (step_3.NumStep === 0) ? 0 : ((r_3 = enable, r_3.A[(r_3.B + step_3.SimStepOld)])))) !== 1)) {
            v = lastOut;
        }
        else if (hasLoad && (((step_5 = step, (step_5.NumStep === 0) ? 0 : ((r_5 = load, r_5.A[(r_5.B + step_5.SimStepOld)])))) === 1)) {
            const step_7 = step;
            if (step_7.NumStep === 0) {
                v = 0;
            }
            else {
                const r_7 = loadData;
                v = (r_7.A[(r_7.B + step_7.SimStepOld)]);
            }
        }
        else {
            const width_1 = width | 0;
            const next = lastOut + 1;
            v = (((width_1 < 32) && (next === ((1 << width_1) >>> 0))) ? 0 : next);
        }
        r_8.A[(r_8.B + step.SimStep)] = v;
    };
}

/**
 * Build the reducer for one component, or None to leave it to fastReduce.
 * One reducer serves both the combinational and the clocked pass. Only the hybrid components -
 * asynchronous RAM, which is reduced once as each - need to tell the two apart, and they are not
 * handled here, so they fall back to EvalReference where the flag still applies. Taking the flag
 * as a parameter here would be worse than useless: it was one, nothing read it, and the caller
 * duly built every reducer twice - which for a ROM meant building and keeping two copies of its
 * lookup table. Anything added here that does depend on the pass must take the flag back.
 */
export function reducerFor(fc) {
    let w, w_1, lsb_3, w_2, lsb_4, w_3, lsb_5, w_4;
    const matchValue = fc.FType;
    const matchValue_1 = fc.UseBigInt;
    let matchResult, cVal, width, gateType, gateType_1, n, lsb, width_1, compareVal, width_2, topWireWidth, lsb0, lsb1, width0, width1, n_5, lsBits, n_8, outputWidths, numberOfBits, width_4, width_5, width_6, width_7, shiftType, width_8, mem_2, mem_3;
    switch (matchValue.tag) {
        case 3: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 0;
            }
            break;
        }
        case 1: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 0;
            }
            break;
        }
        case 2: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 0;
            }
            break;
        }
        case 0: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 1;
            }
            break;
        }
        case 4: {
            matchResult = 2;
            break;
        }
        case 7: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 3;
                cVal = matchValue.fields[1];
                width = matchValue.fields[0];
            }
            break;
        }
        case 49: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 3;
                cVal = matchValue.fields[1];
                width = matchValue.fields[0];
            }
            break;
        }
        case 8: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 4;
            }
            break;
        }
        case 10: {
            if (matchValue.fields[1] === 2) {
                if (matchValue_1) {
                    matchResult = 39;
                }
                else {
                    matchResult = 5;
                    gateType = matchValue.fields[0];
                }
            }
            else if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 6;
                gateType_1 = matchValue.fields[0];
                n = matchValue.fields[1];
            }
            break;
        }
        case 11: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 7;
            }
            break;
        }
        case 12: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 8;
            }
            break;
        }
        case 13: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 8;
            }
            break;
        }
        case 14: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 9;
            }
            break;
        }
        case 15: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 10;
            }
            break;
        }
        case 16: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 11;
            }
            break;
        }
        case 6: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 12;
                lsb = matchValue.fields[1];
                width_1 = matchValue.fields[0];
            }
            break;
        }
        case 47: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 13;
                compareVal = matchValue.fields[1];
                width_2 = matchValue.fields[0];
            }
            break;
        }
        case 5: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 13;
                compareVal = matchValue.fields[1];
                width_2 = matchValue.fields[0];
            }
            break;
        }
        case 27: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 14;
            }
            break;
        }
        case 29: {
            if (matchValue.fields[0] === 2) {
                if (matchValue_1) {
                    matchResult = 39;
                }
                else {
                    matchResult = 15;
                }
            }
            else if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 18;
                n_5 = matchValue.fields[0];
            }
            break;
        }
        case 28: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 16;
                topWireWidth = matchValue.fields[0];
            }
            break;
        }
        case 30: {
            if (matchValue.fields[0] === 2) {
                if (!isEmpty(matchValue.fields[1])) {
                    if (!isEmpty(tail(matchValue.fields[1]))) {
                        if (isEmpty(tail(tail(matchValue.fields[1])))) {
                            if (!isEmpty(matchValue.fields[2])) {
                                if (!isEmpty(tail(matchValue.fields[2]))) {
                                    if (isEmpty(tail(tail(matchValue.fields[2])))) {
                                        if (matchValue_1) {
                                            matchResult = 39;
                                        }
                                        else {
                                            matchResult = 17;
                                            lsb0 = head(matchValue.fields[2]);
                                            lsb1 = head(tail(matchValue.fields[2]));
                                            width0 = head(matchValue.fields[1]);
                                            width1 = head(tail(matchValue.fields[1]));
                                        }
                                    }
                                    else if (matchValue_1) {
                                        matchResult = 39;
                                    }
                                    else {
                                        matchResult = 19;
                                        lsBits = matchValue.fields[2];
                                        n_8 = matchValue.fields[0];
                                        outputWidths = matchValue.fields[1];
                                    }
                                }
                                else if (matchValue_1) {
                                    matchResult = 39;
                                }
                                else {
                                    matchResult = 19;
                                    lsBits = matchValue.fields[2];
                                    n_8 = matchValue.fields[0];
                                    outputWidths = matchValue.fields[1];
                                }
                            }
                            else if (matchValue_1) {
                                matchResult = 39;
                            }
                            else {
                                matchResult = 19;
                                lsBits = matchValue.fields[2];
                                n_8 = matchValue.fields[0];
                                outputWidths = matchValue.fields[1];
                            }
                        }
                        else if (matchValue_1) {
                            matchResult = 39;
                        }
                        else {
                            matchResult = 19;
                            lsBits = matchValue.fields[2];
                            n_8 = matchValue.fields[0];
                            outputWidths = matchValue.fields[1];
                        }
                    }
                    else if (matchValue_1) {
                        matchResult = 39;
                    }
                    else {
                        matchResult = 19;
                        lsBits = matchValue.fields[2];
                        n_8 = matchValue.fields[0];
                        outputWidths = matchValue.fields[1];
                    }
                }
                else if (matchValue_1) {
                    matchResult = 39;
                }
                else {
                    matchResult = 19;
                    lsBits = matchValue.fields[2];
                    n_8 = matchValue.fields[0];
                    outputWidths = matchValue.fields[1];
                }
            }
            else if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 19;
                lsBits = matchValue.fields[2];
                n_8 = matchValue.fields[0];
                outputWidths = matchValue.fields[1];
            }
            break;
        }
        case 25: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 20;
                numberOfBits = matchValue.fields[0];
            }
            break;
        }
        case 22: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 21;
            }
            break;
        }
        case 24: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 22;
            }
            break;
        }
        case 21: {
            if (matchValue.fields[1] != null) {
                if (matchValue_1) {
                    matchResult = 39;
                }
                else {
                    matchResult = 24;
                }
            }
            else if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 23;
            }
            break;
        }
        case 23: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 25;
            }
            break;
        }
        case 17: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 26;
            }
            break;
        }
        case 19: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 27;
            }
            break;
        }
        case 18: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 28;
            }
            break;
        }
        case 20: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 29;
            }
            break;
        }
        case 31: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 30;
            }
            break;
        }
        case 33: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 30;
            }
            break;
        }
        case 32: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 31;
            }
            break;
        }
        case 34: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 31;
            }
            break;
        }
        case 35: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 32;
                width_4 = matchValue.fields[0];
            }
            break;
        }
        case 37: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 33;
                width_5 = matchValue.fields[0];
            }
            break;
        }
        case 36: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 34;
                width_6 = matchValue.fields[0];
            }
            break;
        }
        case 38: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 35;
                width_7 = matchValue.fields[0];
            }
            break;
        }
        case 46: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else {
                matchResult = 36;
                shiftType = matchValue.fields[2];
                width_8 = matchValue.fields[0];
            }
            break;
        }
        case 39: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else if (matchValue.fields[0].AddressWidth <= maxRomTableAddressWidth) {
                matchResult = 37;
                mem_2 = matchValue.fields[0];
            }
            else {
                matchResult = 39;
            }
            break;
        }
        case 40: {
            if (matchValue_1) {
                matchResult = 39;
            }
            else if (matchValue.fields[0].AddressWidth <= maxRomTableAddressWidth) {
                matchResult = 38;
                mem_3 = matchValue.fields[0];
            }
            else {
                matchResult = 39;
            }
            break;
        }
        default:
            matchResult = 39;
    }
    switch (matchResult) {
        case 0: {
            let src;
            const fc_1 = fc;
            src = (new U32Region(item(0, fc_1.InputLinks).UInt32Slab, item(0, fc_1.InputLinks).StepBase));
            let dst;
            const fc_2 = fc;
            dst = (new U32Region(item(0, fc_2.Outputs).UInt32Slab, item(0, fc_2.Outputs).StepBase));
            return (step) => {
                const r_1 = dst;
                let v;
                const r = src;
                v = (r.A[(r.B + step.SimStep)]);
                r_1.A[(r_1.B + step.SimStep)] = v;
            };
        }
        case 1: {
            let src_1;
            const fc_3 = fc;
            src_1 = (new U32Region(item(0, fc_3.InputLinks).UInt32Slab, item(0, fc_3.InputLinks).StepBase));
            let dst_1;
            const fc_4 = fc;
            dst_1 = (new U32Region(item(0, fc_4.Outputs).UInt32Slab, item(0, fc_4.Outputs).StepBase));
            return (step_3) => {
                if (fc.Active) {
                    const r_3 = dst_1;
                    let v_1;
                    const r_2 = src_1;
                    v_1 = (r_2.A[(r_2.B + step_3.SimStep)]);
                    r_3.A[(r_3.B + step_3.SimStep)] = v_1;
                }
            };
        }
        case 2:
            return (_arg) => {
            };
        case 3: {
            let dst_2;
            const fc_5 = fc;
            dst_2 = (new U32Region(item(0, fc_5.Outputs).UInt32Slab, item(0, fc_5.Outputs).StepBase));
            const value = toUInt32(twosComp(width, cVal)) >>> 0;
            return (step_6) => {
                const r_4 = dst_2;
                r_4.A[(r_4.B + step_6.SimStep)] = value;
            };
        }
        case 4: {
            let src_2;
            const fc_6 = fc;
            src_2 = (new U32Region(item(0, fc_6.InputLinks).UInt32Slab, item(0, fc_6.InputLinks).StepBase));
            let dst_3;
            const fc_7 = fc;
            dst_3 = (new U32Region(item(0, fc_7.Outputs).UInt32Slab, item(0, fc_7.Outputs).StepBase));
            return (step_8) => {
                let r_5;
                const r_6 = dst_3;
                const v_3 = (((r_5 = src_2, r_5.A[(r_5.B + step_8.SimStep)])) ^ 1) >>> 0;
                r_6.A[(r_6.B + step_8.SimStep)] = v_3;
            };
        }
        case 5: {
            let a;
            const fc_8 = fc;
            a = (new U32Region(item(0, fc_8.InputLinks).UInt32Slab, item(0, fc_8.InputLinks).StepBase));
            let b;
            const fc_9 = fc;
            b = (new U32Region(item(1, fc_9.InputLinks).UInt32Slab, item(1, fc_9.InputLinks).StepBase));
            let dst_4;
            const fc_10 = fc;
            dst_4 = (new U32Region(item(0, fc_10.Outputs).UInt32Slab, item(0, fc_10.Outputs).StepBase));
            switch (gateType) {
                case "or":
                    return (step_15) => {
                        let r_10, r_11;
                        const r_12 = dst_4;
                        const v_5 = (((r_10 = a, r_10.A[(r_10.B + step_15.SimStep)])) | ((r_11 = b, r_11.A[(r_11.B + step_15.SimStep)]))) >>> 0;
                        r_12.A[(r_12.B + step_15.SimStep)] = v_5;
                    };
                case "xor":
                    return (step_19) => {
                        let r_13, r_14;
                        const r_15 = dst_4;
                        const v_6 = (((r_13 = a, r_13.A[(r_13.B + step_19.SimStep)])) ^ ((r_14 = b, r_14.A[(r_14.B + step_19.SimStep)]))) >>> 0;
                        r_15.A[(r_15.B + step_19.SimStep)] = v_6;
                    };
                case "nand":
                    return (step_23) => {
                        let r_16, r_17;
                        const r_18 = dst_4;
                        const v_7 = (((((r_16 = a, r_16.A[(r_16.B + step_23.SimStep)])) & ((r_17 = b, r_17.A[(r_17.B + step_23.SimStep)]))) >>> 0) ^ 1) >>> 0;
                        r_18.A[(r_18.B + step_23.SimStep)] = v_7;
                    };
                case "nor":
                    return (step_27) => {
                        let r_19, r_20;
                        const r_21 = dst_4;
                        const v_8 = (((((r_19 = a, r_19.A[(r_19.B + step_27.SimStep)])) | ((r_20 = b, r_20.A[(r_20.B + step_27.SimStep)]))) >>> 0) ^ 1) >>> 0;
                        r_21.A[(r_21.B + step_27.SimStep)] = v_8;
                    };
                case "xnor":
                    return (step_31) => {
                        let r_22, r_23;
                        const r_24 = dst_4;
                        const v_9 = (((((r_22 = a, r_22.A[(r_22.B + step_31.SimStep)])) ^ ((r_23 = b, r_23.A[(r_23.B + step_31.SimStep)]))) >>> 0) ^ 1) >>> 0;
                        r_24.A[(r_24.B + step_31.SimStep)] = v_9;
                    };
                default:
                    return (step_11) => {
                        let r_7, r_8;
                        const r_9 = dst_4;
                        const v_4 = (((r_7 = a, r_7.A[(r_7.B + step_11.SimStep)])) & ((r_8 = b, r_8.A[(r_8.B + step_11.SimStep)]))) >>> 0;
                        r_9.A[(r_9.B + step_11.SimStep)] = v_4;
                    };
            }
        }
        case 6: {
            const ins = initialize(n, (i_10) => {
                const fc_11 = fc;
                const i_11 = i_10 | 0;
                return new U32Region(item(i_11, fc_11.InputLinks).UInt32Slab, item(i_11, fc_11.InputLinks).StepBase);
            });
            let dst_5;
            const fc_12 = fc;
            dst_5 = (new U32Region(item(0, fc_12.Outputs).UInt32Slab, item(0, fc_12.Outputs).StepBase));
            const negated = (gateType_1 === "nor") ? true : ((gateType_1 === "xnor") ? true : ((gateType_1 === "and") ? false : ((gateType_1 === "or") ? false : (!(gateType_1 === "xor")))));
            const combine = (gateType_1 === "nand") ? ((x) => ((y) => ((x & y) >>> 0))) : ((gateType_1 === "or") ? ((x_1) => ((y_1) => ((x_1 | y_1) >>> 0))) : ((gateType_1 === "nor") ? ((x_1) => ((y_1) => ((x_1 | y_1) >>> 0))) : ((gateType_1 === "xor") ? ((x_2) => ((y_2) => ((x_2 ^ y_2) >>> 0))) : ((gateType_1 === "xnor") ? ((x_2) => ((y_2) => ((x_2 ^ y_2) >>> 0))) : ((x) => ((y) => ((x & y) >>> 0)))))));
            return (step_35) => {
                const s = step_35.SimStep | 0;
                let acc;
                const r_25 = ins[0];
                acc = (r_25.A[(r_25.B + s)]);
                for (let i_13 = 1; i_13 <= (n - 1); i_13++) {
                    let r_26;
                    acc = combine(acc)((r_26 = (ins[i_13]), r_26.A[(r_26.B + s)]));
                }
                const r_27 = dst_5;
                const v_10 = negated ? ((acc ^ 1) >>> 0) : acc;
                r_27.A[(r_27.B + s)] = v_10;
            };
        }
        case 7: {
            let a_1;
            const fc_13 = fc;
            a_1 = (new U32Region(item(0, fc_13.InputLinks).UInt32Slab, item(0, fc_13.InputLinks).StepBase));
            let b_1;
            const fc_14 = fc;
            b_1 = (new U32Region(item(1, fc_14.InputLinks).UInt32Slab, item(1, fc_14.InputLinks).StepBase));
            let sel;
            const fc_15 = fc;
            sel = (new U32Region(item(2, fc_15.InputLinks).UInt32Slab, item(2, fc_15.InputLinks).StepBase));
            let dst_6;
            const fc_16 = fc;
            dst_6 = (new U32Region(item(0, fc_16.Outputs).UInt32Slab, item(0, fc_16.Outputs).StepBase));
            return (step_39) => {
                let r_28;
                const s_1 = step_39.SimStep | 0;
                const r_31 = dst_6;
                let v_11;
                if (((r_28 = sel, r_28.A[(r_28.B + s_1)])) === 0) {
                    const r_29 = a_1;
                    v_11 = (r_29.A[(r_29.B + s_1)]);
                }
                else {
                    const r_30 = b_1;
                    v_11 = (r_30.A[(r_30.B + s_1)]);
                }
                r_31.A[(r_31.B + s_1)] = v_11;
            };
        }
        case 8: {
            const n_1 = ((fc.FType.tag === 12) ? 4 : 8) | 0;
            const ins_1 = initialize(n_1, (i_18) => {
                const fc_17 = fc;
                const i_19 = i_18 | 0;
                return new U32Region(item(i_19, fc_17.InputLinks).UInt32Slab, item(i_19, fc_17.InputLinks).StepBase);
            });
            let sel_1;
            const fc_18 = fc;
            const i_20 = n_1 | 0;
            sel_1 = (new U32Region(item(i_20, fc_18.InputLinks).UInt32Slab, item(i_20, fc_18.InputLinks).StepBase));
            let dst_7;
            const fc_19 = fc;
            dst_7 = (new U32Region(item(0, fc_19.Outputs).UInt32Slab, item(0, fc_19.Outputs).StepBase));
            return (step_44) => {
                let r_32;
                const s_2 = step_44.SimStep | 0;
                const r_34 = dst_7;
                let v_12;
                const r_33 = ins_1[~~((r_32 = sel_1, r_32.A[(r_32.B + s_2)]))];
                v_12 = (r_33.A[(r_33.B + s_2)]);
                r_34.A[(r_34.B + s_2)] = v_12;
            };
        }
        case 9:
            return demuxU(fc, 2);
        case 10:
            return demuxU(fc, 4);
        case 11:
            return demuxU(fc, 8);
        case 12: {
            let src_3;
            const fc_20 = fc;
            src_3 = (new U32Region(item(0, fc_20.InputLinks).UInt32Slab, item(0, fc_20.InputLinks).StepBase));
            let dst_8;
            const fc_21 = fc;
            dst_8 = (new U32Region(item(0, fc_21.Outputs).UInt32Slab, item(0, fc_21.Outputs).StepBase));
            let patternInput;
            const lsb_1 = lsb | 0;
            patternInput = [lsb_1, (w = (((((lsb + width_1) - 1) - lsb_1) + 1) | 0), (w === 32) ? 4294967295 : (((1 << w) >>> 0) - 1))];
            return (step_48) => {
                let r_35;
                const r_36 = dst_8;
                const v_13 = ((((r_35 = src_3, r_35.A[(r_35.B + step_48.SimStep)])) >>> patternInput[0]) & patternInput[1]) >>> 0;
                r_36.A[(r_36.B + step_48.SimStep)] = v_13;
            };
        }
        case 13: {
            let src_4;
            const fc_22 = fc;
            src_4 = (new U32Region(item(0, fc_22.InputLinks).UInt32Slab, item(0, fc_22.InputLinks).StepBase));
            let dst_9;
            const fc_23 = fc;
            dst_9 = (new U32Region(item(0, fc_23.Outputs).UInt32Slab, item(0, fc_23.Outputs).StepBase));
            if ((compare(compareVal, fromZero()) >= 0) && (compare(compareVal, fromInt64(4294967295n)) <= 0)) {
                const target = toUInt32(compareVal) >>> 0;
                return (step_51) => {
                    let r_37;
                    const r_38 = dst_9;
                    const v_14 = (((r_37 = src_4, r_37.A[(r_37.B + step_51.SimStep)])) === target) ? 1 : 0;
                    r_38.A[(r_38.B + step_51.SimStep)] = v_14;
                };
            }
            else {
                return (step_54) => {
                    const r_39 = dst_9;
                    r_39.A[(r_39.B + step_54.SimStep)] = 0;
                };
            }
        }
        case 14:
            return merge2U(fc, item(0, fc.InputLinks).Width);
        case 15:
            return merge2U(fc, item(0, fc.InputLinks).Width);
        case 16:
            return split2U(fc, [0, (w_1 = ((((topWireWidth - 1) - 0) + 1) | 0), (w_1 === 32) ? 4294967295 : (((1 << w_1) >>> 0) - 1))], (lsb_3 = (topWireWidth | 0), [lsb_3, (w_2 = ((((item(0, fc.InputLinks).Width - 1) - lsb_3) + 1) | 0), (w_2 === 32) ? 4294967295 : (((1 << w_2) >>> 0) - 1))]));
        case 17:
            return split2U(fc, (lsb_4 = (lsb0 | 0), [lsb_4, (w_3 = (((((width0 + lsb0) - 1) - lsb_4) + 1) | 0), (w_3 === 32) ? 4294967295 : (((1 << w_3) >>> 0) - 1))]), (lsb_5 = (lsb1 | 0), [lsb_5, (w_4 = (((((width1 + lsb1) - 1) - lsb_5) + 1) | 0), (w_4 === 32) ? 4294967295 : (((1 << w_4) >>> 0) - 1))]));
        case 18: {
            const ins_2 = initialize(n_5, (i_26) => {
                const fc_24 = fc;
                const i_27 = i_26 | 0;
                return new U32Region(item(i_27, fc_24.InputLinks).UInt32Slab, item(i_27, fc_24.InputLinks).StepBase);
            });
            const widths = initialize(n_5, (n_6) => (item(n_6, fc.InputLinks).Width | 0), Int32Array);
            let dst_10;
            const fc_25 = fc;
            dst_10 = (new U32Region(item(0, fc_25.Outputs).UInt32Slab, item(0, fc_25.Outputs).StepBase));
            return (step_56) => {
                const s_3 = step_56.SimStep | 0;
                let acc_1 = 0;
                for (let i_29 = n_5 - 1; i_29 >= 0; i_29--) {
                    let r_40;
                    acc_1 = ((((acc_1 << (widths[i_29])) >>> 0) | ((r_40 = (ins_2[i_29]), r_40.A[(r_40.B + s_3)]))) >>> 0);
                }
                const r_41 = dst_10;
                const v_16 = acc_1;
                r_41.A[(r_41.B + s_3)] = v_16;
            };
        }
        case 19: {
            let src_5;
            const fc_26 = fc;
            src_5 = (new U32Region(item(0, fc_26.InputLinks).UInt32Slab, item(0, fc_26.InputLinks).StepBase));
            const outs = initialize(n_8, (i_31) => {
                const fc_27 = fc;
                const i_32 = i_31 | 0;
                return new U32Region(item(i_32, fc_27.Outputs).UInt32Slab, item(i_32, fc_27.Outputs).StepBase);
            });
            const slices = toArray(map2((width_3, lsb_6) => {
                let w_5;
                return [lsb_6, (w_5 = (width_3 | 0), (w_5 === 32) ? 4294967295 : (((1 << w_5) >>> 0) - 1))];
            }, outputWidths, lsBits));
            return (step_59) => {
                const s_4 = step_59.SimStep | 0;
                let bits;
                const r_42 = src_5;
                bits = (r_42.A[(r_42.B + s_4)]);
                for (let i_33 = 0; i_33 <= (n_8 - 1); i_33++) {
                    const patternInput_1 = slices[i_33];
                    const r_43 = outs[i_33];
                    const v_17 = ((bits >>> patternInput_1[0]) & patternInput_1[1]) >>> 0;
                    r_43.A[(r_43.B + s_4)] = v_17;
                }
            };
        }
        case 20: {
            let src_6;
            const fc_28 = fc;
            src_6 = (new U32Region(item(0, fc_28.InputLinks).UInt32Slab, item(0, fc_28.InputLinks).StepBase));
            let dst_11;
            const fc_29 = fc;
            dst_11 = (new U32Region(item(0, fc_29.Outputs).UInt32Slab, item(0, fc_29.Outputs).StepBase));
            let allOnes;
            const w_6 = numberOfBits | 0;
            allOnes = ((w_6 === 32) ? 4294967295 : (((1 << w_6) >>> 0) - 1));
            return (step_62) => {
                let r_44;
                const r_45 = dst_11;
                const v_18 = (((r_44 = src_6, r_44.A[(r_44.B + step_62.SimStep)])) === 0) ? 0 : allOnes;
                r_45.A[(r_45.B + step_62.SimStep)] = v_18;
            };
        }
        case 21: {
            let a_2;
            const fc_30 = fc;
            a_2 = (new U32Region(item(0, fc_30.InputLinks).UInt32Slab, item(0, fc_30.InputLinks).StepBase));
            let b_2;
            const fc_31 = fc;
            b_2 = (new U32Region(item(1, fc_31.InputLinks).UInt32Slab, item(1, fc_31.InputLinks).StepBase));
            let dst_12;
            const fc_32 = fc;
            dst_12 = (new U32Region(item(0, fc_32.Outputs).UInt32Slab, item(0, fc_32.Outputs).StepBase));
            return (step_65) => {
                let r_46, r_47;
                const r_48 = dst_12;
                const v_19 = (((r_46 = a_2, r_46.A[(r_46.B + step_65.SimStep)])) & ((r_47 = b_2, r_47.A[(r_47.B + step_65.SimStep)]))) >>> 0;
                r_48.A[(r_48.B + step_65.SimStep)] = v_19;
            };
        }
        case 22: {
            let a_3;
            const fc_33 = fc;
            a_3 = (new U32Region(item(0, fc_33.InputLinks).UInt32Slab, item(0, fc_33.InputLinks).StepBase));
            let b_3;
            const fc_34 = fc;
            b_3 = (new U32Region(item(1, fc_34.InputLinks).UInt32Slab, item(1, fc_34.InputLinks).StepBase));
            let dst_13;
            const fc_35 = fc;
            dst_13 = (new U32Region(item(0, fc_35.Outputs).UInt32Slab, item(0, fc_35.Outputs).StepBase));
            return (step_69) => {
                let r_49, r_50;
                const r_51 = dst_13;
                const v_20 = (((r_49 = a_3, r_49.A[(r_49.B + step_69.SimStep)])) | ((r_50 = b_3, r_50.A[(r_50.B + step_69.SimStep)]))) >>> 0;
                r_51.A[(r_51.B + step_69.SimStep)] = v_20;
            };
        }
        case 23: {
            let a_4;
            const fc_36 = fc;
            a_4 = (new U32Region(item(0, fc_36.InputLinks).UInt32Slab, item(0, fc_36.InputLinks).StepBase));
            let b_4;
            const fc_37 = fc;
            b_4 = (new U32Region(item(1, fc_37.InputLinks).UInt32Slab, item(1, fc_37.InputLinks).StepBase));
            let dst_14;
            const fc_38 = fc;
            dst_14 = (new U32Region(item(0, fc_38.Outputs).UInt32Slab, item(0, fc_38.Outputs).StepBase));
            return (step_73) => {
                let r_52, r_53;
                const r_54 = dst_14;
                const v_21 = (((r_52 = a_4, r_52.A[(r_52.B + step_73.SimStep)])) ^ ((r_53 = b_4, r_53.A[(r_53.B + step_73.SimStep)]))) >>> 0;
                r_54.A[(r_54.B + step_73.SimStep)] = v_21;
            };
        }
        case 24: {
            let a_5;
            const fc_39 = fc;
            a_5 = (new U32Region(item(0, fc_39.InputLinks).UInt32Slab, item(0, fc_39.InputLinks).StepBase));
            let b_5;
            const fc_40 = fc;
            b_5 = (new U32Region(item(1, fc_40.InputLinks).UInt32Slab, item(1, fc_40.InputLinks).StepBase));
            let dst_15;
            const fc_41 = fc;
            dst_15 = (new U32Region(item(0, fc_41.Outputs).UInt32Slab, item(0, fc_41.Outputs).StepBase));
            const w_7 = item(0, fc.InputLinks).Width | 0;
            if (w_7 === 32) {
                return (step_77) => {
                    let r_55, r_56;
                    const r_57 = dst_15;
                    const v_22 = ((r_55 = a_5, r_55.A[(r_55.B + step_77.SimStep)])) * ((r_56 = b_5, r_56.A[(r_56.B + step_77.SimStep)]));
                    r_57.A[(r_57.B + step_77.SimStep)] = v_22;
                };
            }
            else {
                let mask_2;
                const w_8 = w_7 | 0;
                mask_2 = ((w_8 === 32) ? 4294967295 : (((1 << w_8) >>> 0) - 1));
                return (step_81) => {
                    let r_58, r_59;
                    const r_60 = dst_15;
                    const v_23 = ((((r_58 = a_5, r_58.A[(r_58.B + step_81.SimStep)])) * ((r_59 = b_5, r_59.A[(r_59.B + step_81.SimStep)]))) & mask_2) >>> 0;
                    r_60.A[(r_60.B + step_81.SimStep)] = v_23;
                };
            }
        }
        case 25: {
            let src_7;
            const fc_42 = fc;
            src_7 = (new U32Region(item(0, fc_42.InputLinks).UInt32Slab, item(0, fc_42.InputLinks).StepBase));
            let dst_16;
            const fc_43 = fc;
            dst_16 = (new U32Region(item(0, fc_43.Outputs).UInt32Slab, item(0, fc_43.Outputs).StepBase));
            const w_9 = item(0, fc.InputLinks).Width | 0;
            if (w_9 === 32) {
                return (step_85) => {
                    let r_61;
                    const r_62 = dst_16;
                    const v_24 = ~((r_61 = src_7, r_61.A[(r_61.B + step_85.SimStep)])) >>> 0;
                    r_62.A[(r_62.B + step_85.SimStep)] = v_24;
                };
            }
            else {
                let mask_3;
                const w_10 = w_9 | 0;
                mask_3 = ((w_10 === 32) ? 4294967295 : (((1 << w_10) >>> 0) - 1));
                return (step_88) => {
                    let r_63;
                    const r_64 = dst_16;
                    const v_25 = (mask_3 & (~((r_63 = src_7, r_63.A[(r_63.B + step_88.SimStep)])) >>> 0)) >>> 0;
                    r_64.A[(r_64.B + step_88.SimStep)] = v_25;
                };
            }
        }
        case 26:
            return adderU(fc, true, true);
        case 27:
            return adderU(fc, true, false);
        case 28:
            return adderU(fc, false, true);
        case 29:
            return adderU(fc, false, false);
        case 30: {
            let src_8;
            const fc_44 = fc;
            src_8 = (new U32Region(item(0, fc_44.InputLinks).UInt32Slab, item(0, fc_44.InputLinks).StepBase));
            let dst_17;
            const fc_45 = fc;
            dst_17 = (new U32Region(item(0, fc_45.Outputs).UInt32Slab, item(0, fc_45.Outputs).StepBase));
            return (step_91) => {
                const r_67 = dst_17;
                let v_26;
                const step_92 = step_91;
                if (step_92.NumStep === 0) {
                    v_26 = 0;
                }
                else {
                    const r_66 = src_8;
                    v_26 = (r_66.A[(r_66.B + step_92.SimStepOld)]);
                }
                r_67.A[(r_67.B + step_91.SimStep)] = v_26;
            };
        }
        case 31: {
            let src_9;
            const fc_46 = fc;
            src_9 = (new U32Region(item(0, fc_46.InputLinks).UInt32Slab, item(0, fc_46.InputLinks).StepBase));
            let enable;
            const fc_47 = fc;
            enable = (new U32Region(item(1, fc_47.InputLinks).UInt32Slab, item(1, fc_47.InputLinks).StepBase));
            let dst_18;
            const fc_48 = fc;
            dst_18 = (new U32Region(item(0, fc_48.Outputs).UInt32Slab, item(0, fc_48.Outputs).StepBase));
            return (step_95) => {
                let step_96, r_69;
                const r_74 = dst_18;
                let v_27;
                if (((step_96 = step_95, (step_96.NumStep === 0) ? 0 : ((r_69 = enable, r_69.A[(r_69.B + step_96.SimStepOld)])))) === 1) {
                    const step_98 = step_95;
                    if (step_98.NumStep === 0) {
                        v_27 = 0;
                    }
                    else {
                        const r_71 = src_9;
                        v_27 = (r_71.A[(r_71.B + step_98.SimStepOld)]);
                    }
                }
                else {
                    const step_100 = step_95;
                    if (step_100.NumStep === 0) {
                        v_27 = 0;
                    }
                    else {
                        const r_73 = dst_18;
                        v_27 = (r_73.A[(r_73.B + step_100.SimStepOld)]);
                    }
                }
                r_74.A[(r_74.B + step_95.SimStep)] = v_27;
            };
        }
        case 32:
            return counterU(fc, width_4, true, true);
        case 33:
            return counterU(fc, width_5, true, false);
        case 34:
            return counterU(fc, width_6, false, true);
        case 35:
            return counterU(fc, width_7, false, false);
        case 36: {
            let src_10;
            const fc_49 = fc;
            src_10 = (new U32Region(item(0, fc_49.InputLinks).UInt32Slab, item(0, fc_49.InputLinks).StepBase));
            let amtIn;
            const fc_50 = fc;
            amtIn = (new U32Region(item(1, fc_50.InputLinks).UInt32Slab, item(1, fc_50.InputLinks).StepBase));
            let dst_19;
            const fc_51 = fc;
            dst_19 = (new U32Region(item(0, fc_51.Outputs).UInt32Slab, item(0, fc_51.Outputs).StepBase));
            let mask_4;
            const w_11 = width_8 | 0;
            mask_4 = ((w_11 === 32) ? 4294967295 : (((1 << w_11) >>> 0) - 1));
            const signBit = (width_8 - 1) | 0;
            switch (shiftType.tag) {
                case 1:
                    return (step_107) => {
                        let r_78;
                        const s_6 = step_107.SimStep | 0;
                        const bits_2 = (((r_78 = src_10, r_78.A[(r_78.B + s_6)])) & mask_4) >>> 0;
                        let amt_1;
                        const r_79 = amtIn;
                        amt_1 = (r_79.A[(r_79.B + s_6)]);
                        const r_80 = dst_19;
                        const v_29 = (amt_1 >= (width_8 >>> 0)) ? 0 : (bits_2 >>> ~~amt_1);
                        r_80.A[(r_80.B + s_6)] = v_29;
                    };
                case 2:
                    return (step_111) => {
                        let r_81;
                        const s_7 = step_111.SimStep | 0;
                        const bits_3 = (((r_81 = src_10, r_81.A[(r_81.B + s_7)])) & mask_4) >>> 0;
                        let amt_2;
                        const r_82 = amtIn;
                        amt_2 = (r_82.A[(r_82.B + s_7)]);
                        const signSet = (((bits_3 >>> signBit) & 1) >>> 0) === 1;
                        const r_83 = dst_19;
                        let v_30;
                        if (amt_2 === 0) {
                            v_30 = bits_3;
                        }
                        else if (amt_2 >= (width_8 >>> 0)) {
                            v_30 = (signSet ? mask_4 : 0);
                        }
                        else if (signSet) {
                            const a_6 = ~~amt_2 | 0;
                            v_30 = (((bits_3 >>> a_6) | ((mask_4 & ((mask_4 << (width_8 - a_6)) >>> 0)) >>> 0)) >>> 0);
                        }
                        else {
                            v_30 = (bits_3 >>> ~~amt_2);
                        }
                        r_83.A[(r_83.B + s_7)] = v_30;
                    };
                default:
                    return (step_103) => {
                        let r_75;
                        const s_5 = step_103.SimStep | 0;
                        const bits_1 = (((r_75 = src_10, r_75.A[(r_75.B + s_5)])) & mask_4) >>> 0;
                        let amt;
                        const r_76 = amtIn;
                        amt = (r_76.A[(r_76.B + s_5)]);
                        const r_77 = dst_19;
                        const v_28 = (amt >= (width_8 >>> 0)) ? 0 : ((((bits_1 << ~~amt) >>> 0) & mask_4) >>> 0);
                        r_77.A[(r_77.B + s_5)] = v_28;
                    };
            }
        }
        case 37: {
            let src_11;
            const fc_52 = fc;
            src_11 = (new U32Region(item(0, fc_52.InputLinks).UInt32Slab, item(0, fc_52.InputLinks).StepBase));
            let dst_20;
            const fc_53 = fc;
            dst_20 = (new U32Region(item(0, fc_53.Outputs).UInt32Slab, item(0, fc_53.Outputs).StepBase));
            const table = romTable(mem_2);
            return (step_115) => {
                let r_84;
                const s_8 = step_115.SimStep | 0;
                const r_85 = dst_20;
                const v_31 = table[~~((r_84 = src_11, r_84.A[(r_84.B + s_8)]))];
                r_85.A[(r_85.B + s_8)] = v_31;
            };
        }
        case 38: {
            let src_12;
            const fc_54 = fc;
            src_12 = (new U32Region(item(0, fc_54.InputLinks).UInt32Slab, item(0, fc_54.InputLinks).StepBase));
            let dst_21;
            const fc_55 = fc;
            dst_21 = (new U32Region(item(0, fc_55.Outputs).UInt32Slab, item(0, fc_55.Outputs).StepBase));
            const table_1 = romTable(mem_3);
            return (step_118) => {
                let step_119, r_87;
                const r_88 = dst_21;
                const v_32 = table_1[~~((step_119 = step_118, (step_119.NumStep === 0) ? 0 : ((r_87 = src_12, r_87.A[(r_87.B + step_119.SimStepOld)]))))];
                r_88.A[(r_88.B + step_118.SimStep)] = v_32;
            };
        }
        default:
            return undefined;
    }
}

